import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { HaveNoPositions } from '@flipster/features/src/shared/position/have-no-positions';
import { hstack } from '@flipster/styled-system/patterns';
import { Col, Table, TableHeader } from '@flipster/ui/Table';

import { positionTableCss } from '../table-styles';
import { openPositionTableDataBunja } from './bunja';
import { resolveCloseFlipHeaderJustify } from './cta-layout';
import { CtaWidthContext, useCtaWidthProvider } from './cta-width-context';
import OpenPositionRow from './open-position-row';

const OpenPositionTable = () => {
  const t = useTranslations();
  const { positionKeysAtom, hasNoPositionsAtom, hasFlippablePositionAtom, isNetPnlModeAtom } =
    useBunja(openPositionTableDataBunja);
  const positionKeys = useAtomValue(positionKeysAtom);
  const hasNoPositions = useAtomValue(hasNoPositionsAtom);
  const isNetPnlMode = useAtomValue(isNetPnlModeAtom);
  // airdrop 포지션은 flip이 불가하므로, flip 가능한 일반 포지션이 하나도 없으면 헤더의 'Flip position' 라벨을 숨긴다
  const hasFlippablePosition = useAtomValue(hasFlippablePositionAtom);
  const { maxWidth, reportRef } = useCtaWidthProvider();

  const columns: Col[] = [
    { key: 'symbol', title: t('Assets.r250401.Symbol'), minWidth: 110 },
    { key: 'qty-value', title: t('Trade.r250827.Qty_or_value'), minWidth: 130 },
    {
      key: 'pnl',
      title: isNetPnlMode ? t('Assets.r250821.Net_p_l') : t('Assets.r250401.Unrealized_pnl'),
      minWidth: 220,
    },
    { key: 'entry-price', title: t('Assets.r250401.Entry_price'), minWidth: 90 },
    { key: 'mark-price', title: t('Assets.r250401.Mark_price'), minWidth: 90 },
    { key: 'liq-price', title: t('Assets.r250401.Liq_price'), minWidth: 90 },
    { key: 'margin', title: t('Trade.r250401.Margin'), minWidth: 90 },
    { key: 'take-profit', title: t('Trade.r250401.Take_profit_label'), minWidth: 90 },
    { key: 'stop-loss', title: t('Trade.r250401.Stop_loss_label'), minWidth: 90 },
    { key: 'leverage', title: t('Assets.r250401.Leverage'), minWidth: 72 },
    {
      key: 'close-flip',
      title: (
        <div
          className={hstack({ justifyContent: resolveCloseFlipHeaderJustify(hasFlippablePosition), ml: 'auto' })}
          style={{ maxWidth: maxWidth || undefined }}
        >
          <p>{t('Trade.r251111.Close')}</p>
          {hasFlippablePosition && <p>{t('Position_flip.r250401.Flip_position')}</p>}
        </div>
      ),
      minWidth: maxWidth || undefined,
      width: '100%',
      align: 'right',
    },
  ];

  const ctaWidthContextValue = useMemo(
    () => ({ maxWidth, reportRef, hasFlippablePosition }),
    [maxWidth, reportRef, hasFlippablePosition],
  );

  if (hasNoPositions) return <HaveNoPositions css={{ mt: 16 }} positionType='open' />;

  return (
    <CtaWidthContext.Provider value={ctaWidthContextValue}>
      <Table
        className={positionTableCss}
        tHeader={<TableHeader columns={columns} />}
        items={positionKeys}
        renderRow={(positionKey, index) => (
          <OpenPositionRow
            key={`${positionKey.symbol}_${positionKey.slot}`}
            symbol={positionKey.symbol}
            slot={positionKey.slot}
            index={index}
          />
        )}
        withHover
      />
    </CtaWidthContext.Provider>
  );
};

export default OpenPositionTable;
